/// Run history + summary stats mirroring mobile/lib/game/handler/game_run_handler.dart.

import { create } from 'zustand';

import * as gameRunRepository from '../data/gameRunRepository';
import type { GameRun } from '../data/gameRunRepository';

export const gameRunPageSize = 25;

export interface GameRunBoard {
  runs: GameRun[];
  gamesPlayed: number;
  averageScore: number;
  mostFearedEnemy: string;
  hasMore: boolean;
  loadingMore: boolean;
  loading: boolean;
  error: string | null;
}

interface GameRunState extends GameRunBoard {
  loadInitial: (showLoading?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to load runs';
}

export const useGameRunStore = create<GameRunState>((set, get) => ({
  runs: [],
  gamesPlayed: 0,
  averageScore: 0,
  mostFearedEnemy: '',
  hasMore: true,
  loadingMore: false,
  loading: true,
  error: null,

  loadInitial: async (showLoading = true) => {
    if (showLoading) set({ loading: true, error: null });
    try {
      const page = await gameRunRepository.listGameRuns(gameRunPageSize, 0);
      set({
        runs: page.runs,
        gamesPlayed: page.gamesPlayed,
        averageScore: page.averageScore,
        mostFearedEnemy: page.mostFearedEnemy,
        hasMore: page.runs.length >= gameRunPageSize,
        loading: false,
        loadingMore: false,
        error: null,
      });
    } catch (error) {
      set({ loading: false, error: messageOf(error) });
    }
  },

  loadMore: async () => {
    const current = get();
    if (!current.hasMore || current.loadingMore) return;
    set({ loadingMore: true, error: null });
    try {
      const page = await gameRunRepository.listGameRuns(gameRunPageSize, current.runs.length);
      set({
        runs: [...current.runs, ...page.runs],
        gamesPlayed: page.gamesPlayed,
        averageScore: page.averageScore,
        mostFearedEnemy: page.mostFearedEnemy,
        hasMore: page.runs.length >= gameRunPageSize,
        loadingMore: false,
      });
    } catch (error) {
      set({ loadingMore: false, error: messageOf(error) });
    }
  },

  refresh: async () => {
    await get().loadInitial(true);
  },
}));
